/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --background: var(--accent-color);
  --control-height: var(--nve-sys-interaction-field-height);
  --cursor: pointer;
  --track-width: 0.5;
  --track-height: var(--nve-ref-size-150);
  --track-border-radius: var(--nve-ref-border-radius-md);
  --track-background: var(--nve-sys-interaction-background);
  --thumb-width: var(--nve-ref-size-400);
  --thumb-height: var(--nve-ref-size-400);
  --thumb-background: var(--nve-sys-text-muted-color);
  --thumb-border: 0 0 0 2px var(--nve-sys-layer-canvas-background);
  width: 100%;
  contain: layout;
}

:host(:state(disabled)) {
  --background: var(--nve-sys-interaction-disabled-background);
}

:host([prominence='muted']) {
  --filter: grayscale(90%);
}

:host(:not(:state(disabled)):hover) [input] {
  --filter: brightness(1.1);
}

:host([prominence='muted']:not(:state(disabled)):hover) [input] {
  --filter: grayscale(90%) brightness(1.1);
}

::slotted(input) {
  /* https://web.dev/accent-color/#guaranteeing-contrast */
  accent-color: var(--accent-color) !important;
  height: calc(var(--track-height) * 3) !important;
  cursor: var(--cursor) !important;
  margin: 0 !important;
  width: var(--control-width) !important;
  background: transparent;
  align-self: center;
  appearance: var(--_appearance);
  position: relative;
  z-index: 2;
}

[input] {
  position: relative;
  filter: none;
}

[input]::before {
  content: '';
  display: block;
  width: 100%;
  height: var(--track-height);
  background: var(--track-background);
  border-radius: var(--track-border-radius);
  filter: var(--filter);
  position: absolute;
  pointer-events: none;
  align-self: center;
  z-index: 0;
  left: 0;
}

[input]::after {
  content: '';
  display: block;
  width: calc(var(--track-width) * 100% - var(--track-width) * var(--thumb-width) / 2);
  height: var(--track-height);
  background: var(--background);
  border-top-left-radius: var(--track-border-radius);
  border-bottom-left-radius: var(--track-border-radius);
  filter: var(--filter);
  position: absolute;
  pointer-events: none;
  align-self: center;
  z-index: 1;
  left: 0;
}

[input]:has(.datalist-labels) {
  flex-direction: column;
}

[input]:has(.datalist-labels)::before {
  margin-block-start: var(--track-height);
}

[input]:has(.datalist-labels)::after {
  margin-block-start: var(--track-height);
}

.datalist-labels {
  display: flex;
  cursor: auto;
  position: relative;
  min-height: calc(var(--nve-ref-size-150) + var(--nve-ref-space-md) + 1em);
}

.datalist-tick {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nve-ref-space-xxs);
  transform: translateX(-50%);
  font-size: var(--nve-ref-font-size-100);
  color: var(--nve-sys-text-muted-color);
  position: absolute;
}

.datalist-tick::before {
  content: '';
  width: var(--nve-ref-size-100);
  height: var(--nve-ref-size-150);
  border-radius: var(--nve-ref-border-radius-md);
  background: var(--nve-sys-interaction-background);
}

:host([orientation='vertical']) {
  width: fit-content;
  height: 100%;
  contain: layout;
}

:host([orientation='vertical']) [internal-host] {
  height: 100%;
}

:host([orientation='vertical']) [input] {
  height: 100%;
}

:host([orientation='vertical']) ::slotted(input) {
  writing-mode: vertical-lr !important;
  direction: rtl !important;
  width: var(--thumb-width) !important;
  height: calc(100% - var(--track-height) * 2) !important;
  margin: var(--track-height) auto !important;
  align-self: auto !important;
}

:host([orientation='vertical']) [input]::before {
  width: var(--track-height);
  height: calc(100% - var(--track-height) * 2);
  left: 50%;
  top: var(--track-height);
  transform: translateX(-50%);
  align-self: auto;
}

:host([orientation='vertical']) [input]::after {
  width: var(--track-height);
  height: calc(var(--track-width) * (100% - var(--track-height) * 2) - var(--track-width) * var(--thumb-height) / 2);
  left: 50%;
  top: auto;
  bottom: var(--track-height);
  transform: translateX(-50%);
  align-self: auto;
  border-radius: var(--nve-ref-border-radius-none) var(--nve-ref-border-radius-none) var(--track-border-radius)
    var(--track-border-radius);
}

:host([orientation='vertical']) [input]:has(.datalist-labels) {
  flex-direction: row;
}

:host([orientation='vertical']) [input]:has(.datalist-labels)::before,
:host([orientation='vertical']) [input]:has(.datalist-labels)::after {
  margin-block-start: 0;
}

:host([orientation='vertical']) .datalist-labels {
  flex-direction: column;
  height: calc(100% - var(--track-height) * 2);
  margin-block: var(--track-height);
  position: absolute;
  left: calc(50% + var(--thumb-width) / 2 + var(--nve-ref-space-xxs) / 2);
}

:host([orientation='vertical']) .datalist-tick {
  flex-direction: row;
  transform: none;
}

:host([orientation='vertical']) .datalist-tick::before {
  width: var(--nve-ref-size-150);
  height: var(--nve-ref-size-100);
}
